import { ReactNode, useEffect, useState } from 'react';
import {
  NotFoundErrorPage,
  coreExtensionData,
  createFrontendModule,
  useApi,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { Progress } from '@backstage/core-components';
import { useRouteRefParams } from '@backstage/core-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { NotFound } from './NotFound';

// Unknown routes and entity tabs that do not exist.
const notFoundPage = SwappableComponentBlueprint.make({
  name: 'core-not-found-error-page',
  params: define =>
    define({ component: NotFoundErrorPage, loader: () => () => <NotFound /> }),
});

/**
 * The catalog entity page shows a warning linking to backstage.io when the entity does not
 * exist, and gives no hook to change it. So check existence first and render our own page
 * instead of the catalog's when the entity is missing.
 */
function MissingEntityGuard({ children }: { children: ReactNode }) {
  const catalogApi = useApi(catalogApiRef);
  const { kind, namespace, name } = useRouteRefParams(entityRouteRef);
  const [state, setState] = useState<{
    loading: boolean;
    exists?: boolean;
    error?: boolean;
  }>({ loading: true });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    catalogApi
      .getEntityByRef({ kind, namespace, name })
      .then(e => !cancelled && setState({ loading: false, exists: !!e }))
      .catch(() => !cancelled && setState({ loading: false, error: true }));
    return () => {
      cancelled = true;
    };
  }, [catalogApi, kind, namespace, name]);
  const { loading, error } = state;
  const value = state.exists;
  if (loading) return <Progress />;
  // On a lookup error fall through to the catalog page, which shows the real error.
  if (!error && !value) {
    return (
      <NotFound
        title="We couldn't find that item"
        message="It may have been deleted, or you may not have access to it."
      />
    );
  }
  return <>{children}</>;
}

const entityPageWithGuard = catalogPlugin
  .getExtension('page:catalog/entity')
  .override({
    factory(originalFactory) {
      const original = originalFactory();
      const element = original.get(coreExtensionData.reactElement);
      return [
        ...[...original].filter(
          v => v.id !== coreExtensionData.reactElement.id,
        ),
        coreExtensionData.reactElement(
          <MissingEntityGuard>{element}</MissingEntityGuard>,
        ),
      ];
    },
  });

export const notFoundModule = createFrontendModule({
  pluginId: 'app',
  extensions: [notFoundPage],
});

export const catalogGuardModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [entityPageWithGuard],
});
